<template>
  <div class="chart-widget" :style="{ height: height + 'px' }">
    <!-- Loader overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loader"></div>
    </div>

    <!-- 🔹 No data message -->
    <div v-else-if="!hasData" class="no-data-message">{{ $t('charts.entrance.noData') }}</div>

    <!-- 🔹 Chart -->
    <PrioChart
      v-else
      :option="chartOptions"
      :active="isActive"
      :height="height"
      name="Entrance"
    />
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'

// core ECharts registration
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'
use([LineChart, GridComponent, TooltipComponent, TitleComponent, LegendComponent, CanvasRenderer])

// safe wrapper
import PrioChart from '@/components/ui/PrioChart.vue'

export default {
  name: 'ChartsDashboardEntrance',
  components: { PrioChart },
  props: {
    chartData: { type: Object, required: true, default: () => ({}) },
    selectedDuration: { type: Number, required: true },
    isActive: { type: Boolean, default: true },
    height: { type: Number, default: 340 }
  },
  setup(props) {
    const { t } = useI18n()
    const isLoading = ref(true)

    // convert { "2025-05-04": 123, ... } → arrays
    const dates = computed(() =>
      Object.keys(props.chartData || {}).map(ts => {
        const d = new Date(ts)
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const yy = d.getFullYear()
        return `${dd}-${mm}-${yy}`
      })
    )
    const counts = computed(() => Object.values(props.chartData || {}))
    const hasData = computed(() => dates.value.length > 0 && counts.value.some(v => v > 0))

    // Dutch numbers read 1.234; English keeps ECharts' own formatting.
    const isEn = () => currentLanguage() === 'en'
    const chartOptions = computed(() => ({
      tooltip: isEn() ? { trigger: 'axis' } : { trigger: 'axis', valueFormatter: v => formatNumber(v) },
      grid: { containLabel: true, top: 40, right: 10, left: 40, bottom: 30 },
      xAxis: {
        type: 'category',
        data: dates.value,
        axisLabel: { rotate: 45, fontSize: 10 }
      },
      yAxis: {
        type: 'value',
        name: t('charts.entrance.visitorsAxis'),
        nameTextStyle: { align: 'left' },
        min: 0,
        ...(isEn() ? {} : { axisLabel: { formatter: v => formatNumber(v) } })
      },
      series: [
        {
          name: t('charts.entrance.visitorCount'),
          type: 'line',
          data: counts.value,
          smooth: true,
          lineStyle: { width: 2 },
          itemStyle: { color: 'rgb(54, 145, 13)' },
          areaStyle: { opacity: 0.1, color: 'rgb(54, 145, 13)' }
        }
      ]
    }))

    //manage loader visibility safely
    watch(
      () => props.chartData,
      async () => {
        isLoading.value = true
        await nextTick()
        // wait a tick for reactivity flush and layout settle
        setTimeout(() => {
          isLoading.value = false
        }, 100)
      },
      { deep: true, immediate: true }
    )

    watch(
      () => props.selectedDuration,
      () => {
        isLoading.value = true
        setTimeout(() => (isLoading.value = false), 150)
      }
    )

    return { chartOptions, isLoading, hasData }
  }
}
</script>

<style scoped>
.chart-widget {
  position: relative;
  width: 100%;
}

.no-data-message {
  color: #7a8895;
  font-size: 13px;
  padding: 8px 0;
  text-align: center;
}

/* Loader overlay preserved from your version */
.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.loader {
  width: 20px;
  padding: 2px;
  margin-top: 10px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #b2c149;
  --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
  mask: var(--_m);
  -webkit-mask-composite: source-out;
  mask-composite: subtract;
  animation: spinner 1s infinite linear;
}

@keyframes spinner {
  to {
    transform: rotate(1turn);
  }
}
</style>